import type { AccommodationTip } from '@/scopes/search/search/infrastructure/api/types';
import { css, styled } from 'styled-components';
import { Icon, tk, Typography } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { LazyImage } from '@/scopes/common/components/Image';
import { ScoreRatings } from './ProductCard/ScoresRatings';
import { LocationTag } from './ProductCard/LocationTag';
import { PriceInfo } from '@/scopes/common/components/PriceInfo';
import { OverlayLink } from './ProductCard/OverlayLink';
import type { RoomsParticipantDistribution } from '@/domain/shared/types';

export interface AccommodationCardProps extends AccommodationTip {
  $orientation?: 'top' | 'bottom';
  slideIndex?: number;
  participants?: RoomsParticipantDistribution;
}

export default function AccommodationCard({
  $orientation,
  arrivalDate,
  departureDate,
  departureDateRaw,
  durationRaw,
  id,
  images,
  locations,
  mealPlan,
  name,
  occupancy,
  price,
  rating,
  slideIndex,
  transportType,
  url,
  userRating,
  userRatingText,
  outboundDepartureAirportName,
  participants,
}: AccommodationCardProps) {
  const intl = useIntl();
  const fallbackText = intl.formatMessage({
    id: 'failed_to_load_image',
  });

  // TODO: Alt text is missing in the API for the LazyImage component.

  return (
    <Card>
      <StyledOverlayLink href={url} aria-label={name} target="_blank" />
      <LazyImage
        src={images[0]}
        alt={name}
        srcSet={[560, 940, 1200]}
        sizes="(min-width: 1280px) 936px, 100vw"
        fallbackText={fallbackText}
        loading={slideIndex && slideIndex > 1 ? 'lazy' : 'eager'}
      />
      <Content $orientation={$orientation}>
        <HeadingWrapper>
          <ScoreRatings rating={rating} userRating={userRating} aria-label={userRatingText} />
          <Heading>{name}</Heading>
          <LocationTag locations={locations} />
        </HeadingWrapper>
        <InfoWrapper>
          <div>
            <Typography as="div" $variant="body-sm">
              {mealPlan}, {occupancy}
            </Typography>
            <Typography as="div" $variant="body-sm-bold">
              {departureDate} - {arrivalDate}
            </Typography>
            {outboundDepartureAirportName && (
              <AirportNameContainer>
                <AirportName>
                  <Icon glyph="IconAirplaneDeparture" size="sm" />
                  <Typography as="div" $variant="body-sm">
                    {outboundDepartureAirportName}
                  </Typography>
                </AirportName>
              </AirportNameContainer>
            )}
          </div>
          <div>
            {price && (
              <StyledPriceInfo
                accommodationId={id}
                departureDateRaw={departureDateRaw}
                durationRaw={durationRaw}
                mealPlan={mealPlan}
                price={price}
                transportType={transportType}
                size="medium"
                participants={participants}
              />
            )}
          </div>
        </InfoWrapper>
      </Content>
    </Card>
  );
}

const Heading = styled(Typography).attrs({ as: 'span', $variant: 'heading-md' })`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  height: 480px;

  @media ${tk('mq_tablet')} {
    height: 320px;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const HeadingWrapper = styled.div`
  margin-bottom: ${tk('spacing_sm')};
`;

const Content = styled.div.attrs<{ $orientation?: 'top' | 'bottom' }>((props) => ({
  $orientation: props.$orientation || 'bottom',
}))`
  position: absolute;
  left: ${tk('spacing_lg')};
  right: ${tk('spacing_lg')};
  background: white;
  border-radius: ${tk('radius_large')};
  padding: ${tk('spacing_lg')};

  ${({ $orientation }) =>
    $orientation === 'top'
      ? css`
          top: ${tk('spacing_lg')};
        `
      : css`
          bottom: ${tk('spacing_2xl')};
        `};

  @media ${tk('mq_tablet')} {
    width: 352px;
    top: ${tk('spacing_3xl')};
    bottom: ${tk('spacing_3xl')};
    left: ${tk('spacing_3xl')};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const StyledOverlayLink = styled(OverlayLink)`
  z-index: 1; // So that the link overlay is on top of the slider
`;

const StyledPriceInfo = styled(PriceInfo)`
  button {
    z-index: 2; // This is a fix for the button not being clickable in the overlay link
  }
`;

const AirportNameContainer = styled.div`
  & * {
    font-size: ${tk('font_size_2xs')};
  }

  font-weight: ${tk('font_weight_medium')};
  color: ${tk('color_semantic_text_strong')};
  display: flex;
  line-height: ${tk('font_line_height_lg')};
  bottom: -12px;
  top: 3px;
  position: relative;
`;

const AirportName = styled.div`
  padding-right: ${tk('spacing_sm')};
  display: flex;
  margin-left: ${tk('spacing_xs')};

  span {
    margin-right: ${tk('spacing_xs')};
    margin-left: -${tk('spacing_xs')};
  }
`;
